"use strict";
import { useReadContracts } from "wagmi";
import { useMemo, useState, useEffect } from "react";
import { isV3PoolType, useVeGaugeData } from "features/veSubgraph/gaugeData";
import { useOnChainGaugeBase } from "./useOnChainGaugeBase";
const POOL_TYPE_ABI = [
  {
    inputs: [],
    name: "tickSpacing",
    outputs: [{ name: "", type: "int24" }],
    stateMutability: "view",
    type: "function"
  }
];
const POOL_TYPE_CACHE_KEY = "pool_type_cache_v1";
function getPoolTypeCache() {
  try {
    const cached = localStorage.getItem(POOL_TYPE_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      return new Map(Object.entries(parsed));
    }
  } catch {
  }
  return /* @__PURE__ */ new Map();
}
function savePoolTypeCache(cache) {
  try {
    const obj = Object.fromEntries(cache);
    localStorage.setItem(POOL_TYPE_CACHE_KEY, JSON.stringify(obj));
  } catch {
  }
}
export function useOnChainGaugeList(chainId, enabled = true) {
  const ve = useVeGaugeData(chainId, enabled);
  const { poolAddresses, gaugeInfoList, isLoading: isLoadingBase } = useOnChainGaugeBase(chainId, enabled);
  const [poolTypeCache, setPoolTypeCache] = useState(() => getPoolTypeCache());
  const uncachedPools = useMemo(() => {
    if (ve.isTrying) {
      return [];
    }
    return poolAddresses.filter((addr) => !poolTypeCache.has(addr));
  }, [ve.isTrying, poolAddresses, poolTypeCache]);
  const poolTypeContracts = useMemo(() => {
    if (uncachedPools.length === 0) return [];
    return uncachedPools.map((poolAddr) => ({
      address: poolAddr,
      abi: POOL_TYPE_ABI,
      functionName: "tickSpacing",
      args: []
    }));
  }, [uncachedPools]);
  const { data: poolTypeData, isLoading: isLoadingPoolTypes } = useReadContracts({
    contracts: poolTypeContracts,
    query: {
      enabled: poolTypeContracts.length > 0,
      staleTime: Infinity,
      // Pool type never changes
      gcTime: Infinity
    }
  });
  useEffect(() => {
    if (!poolTypeData || uncachedPools.length === 0) return;
    const newCache = new Map(poolTypeCache);
    let hasChanges = false;
    uncachedPools.forEach((poolAddr, i) => {
      const result2 = poolTypeData[i];
      const poolType = result2?.status === "success" ? "V3" : "V2";
      newCache.set(poolAddr, poolType);
      hasChanges = true;
    });
    if (hasChanges) {
      setPoolTypeCache(newCache);
      savePoolTypeCache(newCache);
    }
  }, [poolTypeData, uncachedPools]);
  const result = useMemo(() => {
    if (ve.isActive && ve.data) {
      const gauges2 = ve.data.gauges.filter((gauge) => gauge.isAlive).map((gauge) => ({
        id: gauge.id.toLowerCase(),
        pool: gauge.pool.id.toLowerCase(),
        poolType: isV3PoolType(gauge.pool.poolType) ? "V3" : "V2",
        isAlive: gauge.isAlive,
        bribeContract: gauge.bribeVotingReward.id.toLowerCase(),
        feeContract: gauge.feesVotingReward.id.toLowerCase()
      }));
      return {
        gauges: gauges2,
        isLoading: false,
        error: null
      };
    }
    if (ve.isTrying) {
      return {
        gauges: [],
        isLoading: true,
        error: null
      };
    }
    const isLoading = isLoadingBase || uncachedPools.length > 0 && isLoadingPoolTypes;
    if (isLoading) {
      return {
        gauges: [],
        isLoading: true,
        error: null
      };
    }
    const allPoolTypesCached = poolAddresses.every((addr) => poolTypeCache.has(addr));
    if (poolAddresses.length > 0 && !allPoolTypesCached) {
      return {
        gauges: [],
        isLoading: true,
        error: null
      };
    }
    const gauges = gaugeInfoList.filter((detail) => detail.isAlive).map((detail) => ({
      id: detail.gaugeAddress,
      pool: detail.poolAddress,
      poolType: poolTypeCache.get(detail.poolAddress) || "V2",
      isAlive: detail.isAlive,
      bribeContract: detail.bribeContract,
      feeContract: detail.feeContract
    }));
    return {
      gauges,
      isLoading: false,
      error: null
    };
  }, [
    ve.isActive,
    ve.isTrying,
    ve.data,
    isLoadingBase,
    isLoadingPoolTypes,
    uncachedPools.length,
    poolAddresses,
    gaugeInfoList,
    poolTypeCache
  ]);
  return result;
}
